import { createEffect, createSignal, onCleanup, Show } from "solid-js";
import type { TodoStore } from "../lib/store";
import type { Priority, Task } from "../lib/types";
import { Icon } from "./Icon";

export function TaskItem(props: { task: Task; store: TodoStore; position: number; setSize: number }) {
  let editor: HTMLElement | undefined;
  let previousFocus: HTMLElement | undefined;
  const [editing, setEditing] = createSignal(false);
  const [title, setTitle] = createSignal(props.task.title);
  const [notes, setNotes] = createSignal(props.task.notes);
  const [dueDate, setDueDate] = createSignal(props.task.due_date ?? "");
  const [priority, setPriority] = createSignal<Priority>(props.task.priority);
  const project = () => props.store.projects().find((item) => item.id === props.task.project_id);

  function startEdit() {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    setTitle(props.task.title);
    setNotes(props.task.notes);
    setDueDate(props.task.due_date ?? "");
    setPriority(props.task.priority);
    setEditing(true);
  }

  function closeEditor() {
    setEditing(false);
    queueMicrotask(() => previousFocus?.focus());
  }

  async function save() {
    await props.store.updateTask(props.task.id, {
      title: title(),
      notes: notes(),
      due_date: dueDate() || null,
      priority: priority(),
    });
    closeEditor();
  }

  function onEditorKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeEditor();
      return;
    }
    if (event.key !== "Tab" || !editor) return;
    const focusable = Array.from(editor.querySelectorAll<HTMLElement>("button, input, select, textarea"))
      .filter((element) => !element.hasAttribute("disabled"));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  createEffect(() => {
    if (!editing()) return;
    queueMicrotask(() => editor?.querySelector<HTMLInputElement>("input")?.focus());
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    onCleanup(() => {
      document.body.style.overflow = previousOverflow;
    });
  });

  return (
    <div
      class="task-row"
      classList={{ selected: props.store.selectedId() === props.task.id, completed: props.task.completed }}
      role="listitem"
      aria-posinset={props.position}
      aria-setsize={props.setSize}
      tabIndex={0}
      onFocus={() => props.store.setSelectedId(props.task.id)}
      onClick={() => props.store.setSelectedId(props.task.id)}
    >
      <button
        class="check-button"
        aria-label={props.task.completed ? "Mark task incomplete" : "Mark task complete"}
        aria-pressed={props.task.completed}
        onClick={(event) => {
          event.stopPropagation();
          void props.store.updateTask(props.task.id, { completed: !props.task.completed });
        }}
      />

      <button class="task-content" data-edit-task={props.task.id} onClick={startEdit}>
        <span class="task-title-line">
          <span class="task-title">{props.task.title}</span>
          <Show when={props.task.priority !== "none"}>
            <span class={`priority-dot ${props.task.priority}`} title={`${props.task.priority} priority`} />
          </Show>
        </span>
        <span class="task-subline">
          <Show when={props.task.due_date}>
            <time classList={{ overdue: isOverdue(props.task) }} dateTime={props.task.due_date ?? undefined}>
              {formatDueDate(props.task.due_date)}
            </time>
          </Show>
          <Show when={props.task.notes}>
            <span class="task-note">{props.task.notes}</span>
          </Show>
          <Show when={project()}>
            {(current) => (
              <span class="task-project">
                <span style={{ "background-color": current().color }} aria-hidden="true" />
                {current().name}
              </span>
            )}
          </Show>
        </span>
      </button>
      <div class="row-actions">
        <button class="row-icon-button" onClick={startEdit} aria-label={`Edit ${props.task.title}`}>
          <Icon name="edit" size={16} />
        </button>
        <button class="row-icon-button" onClick={() => void props.store.reorderTask(props.task.id, -1)} aria-label={`Move ${props.task.title} up`}>
          <Icon name="chevron-up" size={16} />
        </button>
        <button class="row-icon-button" onClick={() => void props.store.reorderTask(props.task.id, 1)} aria-label={`Move ${props.task.title} down`}>
          <Icon name="chevron-down" size={16} />
        </button>
        <button class="row-icon-button danger" onClick={() => void props.store.deleteTask(props.task.id)} aria-label={`Delete ${props.task.title}`}>
          <Icon name="trash" size={16} />
        </button>
      </div>

      <Show when={editing()}>
        <div class="task-editor-backdrop" role="presentation" onMouseDown={closeEditor}>
          <section
            ref={editor}
            class="task-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`edit-heading-${props.task.id}`}
            onMouseDown={(event) => event.stopPropagation()}
            onKeyDown={onEditorKeyDown}
          >
            <header class="task-editor-header">
              <div>
                <p class="eyebrow">Edit task</p>
                <h2 id={`edit-heading-${props.task.id}`}>Make it clear and easy</h2>
              </div>
              <button class="small-icon-button" type="button" aria-label="Close editor" onClick={closeEditor}>
                <Icon name="close" />
              </button>
            </header>
            <form class="edit-form" onSubmit={(event) => { event.preventDefault(); void save(); }}>
              <label for={`title-${props.task.id}`}>Task name</label>
              <input id={`title-${props.task.id}`} value={title()} onInput={(event) => setTitle(event.currentTarget.value)} maxlength="180" />
              <label for={`notes-${props.task.id}`}>Notes</label>
              <textarea id={`notes-${props.task.id}`} value={notes()} onInput={(event) => setNotes(event.currentTarget.value)} placeholder="Anything helpful to remember?" maxlength="4000" />
              <div class="edit-meta-grid">
                <label>
                  <span>Due date</span>
                  <input type="date" value={dueDate()} onInput={(event) => setDueDate(event.currentTarget.value)} />
                </label>
                <label>
                  <span>Priority</span>
                  <select value={priority()} onChange={(event) => setPriority(event.currentTarget.value as Priority)}>
                    <option value="none">None</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </label>
              </div>
              <div class="editor-actions">
                <button type="button" class="danger-text-button" onClick={() => {
                  closeEditor();
                  void props.store.deleteTask(props.task.id);
                }}>Delete task</button>
                <span />
                <button type="button" class="secondary-button" onClick={closeEditor}>Cancel</button>
                <button type="submit" class="primary-button" disabled={!title().trim()}>Save</button>
              </div>
            </form>
          </section>
        </div>
      </Show>
    </div>
  );
}

function formatDueDate(value: string | null) {
  if (!value) return "";
  const today = new Date();
  const date = new Date(`${value}T00:00:00`);
  const todayKey = localDateKey(today);
  if (value === todayKey) return "Today";
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (value === localDateKey(tomorrow)) return "Tomorrow";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
}

function isOverdue(task: Task) {
  return Boolean(task.due_date && !task.completed && task.due_date < localDateKey(new Date()));
}

function localDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
